/**
 * Baked prop models (public/models/*.glb, generated by scripts/blender/build_props.py)
 * and the static set dressing placed with them. The models load in the
 * background; until they arrive, or if one fails to load, the procedural
 * stand-ins in props.ts stay in place.
 */
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { ktx2 } from './ktx2';
import { sectorTop, type Level, type Sector } from '../sim/grid/level';
import { BLOCK, DIRS, DIR_VEC, DIR_YAW, type Dir } from '../sim/grid/units';

export const PROP_MODELS = [
  'brazier',
  'column_base',
  'column_capital',
  'door',
  'lever',
  'block',
  'idol_jade',
  'idol_gold',
  'idol_stone',
  'relic',
  'altar',
  'medkit',
  'rubble_a',
  'rubble_b',
  'rubble_c',
  'pot_broken',
  'sand_drift',
] as const;
export type PropModel = (typeof PROP_MODELS)[number];

/** Height of the door model (m); shorter doors sink the extra into the floor. */
export const DOOR_MODEL_HEIGHT = 5;
const ONE = new THREE.Vector3(1, 1, 1);
/** Height of the column capital model (m). */
const CAPITAL_HEIGHT = 0.6;

export class PropLibrary {
  private readonly scenes = new Map<PropModel, THREE.Object3D>();

  static async load(baseUrl: string): Promise<PropLibrary> {
    const lib = new PropLibrary();
    const loader = new GLTFLoader();
    // The models' textures are KTX2 (scripts/textures/ktx2.ts).
    const k = ktx2();
    if (k) loader.setKTX2Loader(k);
    await Promise.all(
      PROP_MODELS.map(async (name) => {
        try {
          const gltf = await loader.loadAsync(`${baseUrl}models/${name}.glb`);
          lib.scenes.set(name, gltf.scene);
        } catch {
          // Missing or broken model: the procedural fallback stays.
        }
      }),
    );
    lib.tuneMaterials();
    return lib;
  }

  has(name: PropModel): boolean {
    return this.scenes.has(name);
  }

  /** A new instance; geometry and materials are shared with every other instance. */
  instance(name: PropModel, castShadow = true): THREE.Object3D | null {
    const src = this.scenes.get(name);
    if (!src) return null;
    const o = src.clone(true);
    o.traverse((m) => {
      if (m instanceof THREE.Mesh) {
        m.castShadow = castShadow;
        m.receiveShadow = true;
      }
    });
    return o;
  }

  /**
   * One InstancedMesh per mesh of a model, drawing it at every placement
   * (world matrices of the model's root).
   */
  instancedParts(
    name: PropModel,
    placements: readonly THREE.Matrix4[],
    castShadow: boolean,
  ): THREE.InstancedMesh[] {
    const src = this.scenes.get(name);
    if (!src || !placements.length) return [];
    src.updateMatrixWorld(true);
    const out: THREE.InstancedMesh[] = [];
    const m = new THREE.Matrix4();
    src.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const inst = new THREE.InstancedMesh(o.geometry, o.material, placements.length);
      placements.forEach((p, i) => inst.setMatrixAt(i, m.multiplyMatrices(p, o.matrixWorld)));
      inst.instanceMatrix.needsUpdate = true;
      inst.computeBoundingSphere();
      inst.computeBoundingBox();
      inst.castShadow = castShadow;
      inst.receiveShadow = true;
      inst.name = `${name}:${o.name}`;
      out.push(inst);
    });
    return out;
  }

  /** The shared material of a named mesh inside a model (for glow animation). */
  material(name: PropModel, mesh: string): THREE.MeshStandardMaterial | null {
    const o = this.scenes.get(name)?.getObjectByName(mesh);
    return o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial ? o.material : null;
  }

  /**
   * Emissive parts ship at unit strength; lift them into the bloom range. Sand
   * drifts get no baked indirect light, unlike the floor they lie on, so they
   * are darkened to sit in the same light instead of reading as pale sheets.
   */
  private tuneMaterials(): void {
    const coals = this.material('brazier', 'coals');
    if (coals) coals.emissiveIntensity = 2.8;
    const amber = this.material('door', 'seal_amber');
    if (amber) amber.emissiveIntensity = 1.6;
    const drift = this.material('sand_drift', 'sand_drift');
    if (drift) {
      drift.color.multiplyScalar(0.55);
      drift.roughness = 1;
    }
  }
}

/** Deterministic 0..1 hash of a cell and a salt (render-side dressing only). */
function hash(a: number, b: number, salt: number): number {
  let h = Math.imul(a | 0, 374761393) ^ Math.imul(b | 0, 668265263) ^ Math.imul(salt | 0, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Static dressing for every room: capitals and plinths on freestanding
 * pillars, the altar under the relic, sand drifts along wall feet, rubble in
 * corners and broken pots by the walls. Cells in ``busy`` (entities) are left
 * clear so nothing hides gameplay.
 */
export function dressLevel(level: Level, lib: PropLibrary, busy: ReadonlySet<string>): THREE.Group {
  const g = new THREE.Group();
  g.name = 'dressing';
  const neighbour = (s: Sector, d: Dir): Sector | undefined =>
    level.sector(s.cx + DIR_VEC[d].x, s.cz + DIR_VEC[d].z);
  const isWall = (n: Sector | undefined): boolean => !n || n.wall;
  // Placements are gathered per room and model, then drawn as one instanced mesh per model
  // part: a room's dozens of rubble piles, pots and drifts cost a few draw calls.
  const placements = new Map<string, { name: PropModel; castShadow: boolean; matrices: THREE.Matrix4[] }>();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const put = (
    name: PropModel,
    x: number,
    y: number,
    z: number,
    yaw: number,
    castShadow: boolean,
    scale: THREE.Vector3 = ONE,
  ): void => {
    if (!lib.has(name)) return;
    const room = level.sector(Math.floor(x / BLOCK), Math.floor(z / BLOCK))?.room ?? '';
    const key = `${room}/${name}`;
    let p = placements.get(key);
    if (!p) {
      p = { name, castShadow, matrices: [] };
      placements.set(key, p);
    }
    p.matrices.push(
      new THREE.Matrix4().compose(new THREE.Vector3(x, y, z), q.setFromAxisAngle(up, yaw), scale),
    );
  };

  for (const s of level.allSectors()) {
    const cx = s.cx * BLOCK + BLOCK / 2;
    const cz = s.cz * BLOCK + BLOCK / 2;
    if (s.wall) {
      // A freestanding pillar: plinth at its foot, corbelled capital under the ceiling.
      const open = DIRS.map((d) => neighbour(s, d)).filter((n): n is Sector => !!n && !n.wall);
      if (open.length >= 3) {
        const floor = Math.min(...open.map(sectorTop));
        const ceil = Math.min(...open.map((n) => n.ceil));
        put('column_base', cx, floor, cz, 0, true);
        if (ceil - floor > 3) put('column_capital', cx, ceil - CAPITAL_HEIGHT, cz, 0, true);
      }
      continue;
    }
    const top = sectorTop(s);
    const flat = Math.max(...s.floor) - Math.min(...s.floor) < 1e-3;
    if (!flat || s.pit || s.water !== null || s.flags.size > 0 || busy.has(`${s.cx},${s.cz}`)) continue;
    const walls = DIRS.filter((d) => isWall(neighbour(s, d)));

    // Sand drifts at the foot of walls on sand floors (their ends match, so neighbours chain).
    walls.forEach((d, i) => {
      if (s.mat === 'sand' && hash(s.cx, s.cz, 11 + i) < 0.34) {
        const v = DIR_VEC[d];
        put('sand_drift', cx + (v.x * BLOCK) / 2, top, cz + (v.z * BLOCK) / 2, DIR_YAW[d], false);
      }
    });

    // Rubble in corners.
    const [w0, w1] = walls;
    if (walls.length === 2 && w0 && w1 && DIR_VEC[w0].x !== DIR_VEC[w1].x) {
      if (hash(s.cx, s.cz, 23) < 0.3) {
        const a = DIR_VEC[w0];
        const b = DIR_VEC[w1];
        const rubble: PropModel[] = ['rubble_a', 'rubble_b', 'rubble_c'];
        const pick = rubble[Math.floor(hash(s.cx, s.cz, 29) * 3)] ?? 'rubble_c';
        const yaw = hash(s.cx, s.cz, 31) * Math.PI * 2;
        put(pick, cx + (a.x + b.x) * 0.42, top, cz + (a.z + b.z) * 0.42, yaw, true);
      }
      continue;
    }
    // Broken pots by a single wall.
    if (walls.length === 1 && w0 && hash(s.cx, s.cz, 37) < 0.1) {
      const v = DIR_VEC[w0];
      put('pot_broken', cx + v.x * 0.5, top, cz + v.z * 0.5, hash(s.cx, s.cz, 41) * Math.PI * 2, true);
    }
  }

  // The relic's dais: dress each raised cell (a 1 m step) as an altar.
  for (const e of level.entities) {
    if (e.type !== 'relic') continue;
    const start = level.sector(e.at[0], e.at[1]);
    if (!start) continue;
    const h = sectorTop(start);
    const seen = new Set<string>();
    const todo: Sector[] = [start];
    while (todo.length) {
      const s = todo.pop() as Sector;
      const k = `${s.cx},${s.cz}`;
      if (seen.has(k) || s.wall || Math.abs(sectorTop(s) - h) > 1e-3) continue;
      seen.add(k);
      const around = DIRS.map((d) => neighbour(s, d)).filter((n): n is Sector => !!n && !n.wall);
      const low = Math.min(...around.map(sectorTop), h);
      if (h - low > 0.75 && h - low < 1.25) {
        // Top 6 mm above the step and 1 % wider than the cell so it never z-fights the level mesh.
        put(
          'altar',
          s.cx * BLOCK + BLOCK / 2,
          low,
          s.cz * BLOCK + BLOCK / 2,
          0,
          true,
          new THREE.Vector3(1.012, h - low + 0.006, 1.012),
        );
      }
      around.forEach((n) => todo.push(n));
    }
  }
  for (const p of placements.values()) {
    for (const mesh of lib.instancedParts(p.name, p.matrices, p.castShadow)) g.add(mesh);
  }
  return g;
}
